import { Button, Flex, Stack, Switch } from '@eleveo/components-react';
import { BinIcon, PlusIcon } from '@eleveo/icons-react';
import React, { useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';

import { AzureConnectionString } from './AzureConnectionStrings';
import { useIntegrations } from '../../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../../constants/dataTestIds';
import { getSupportedStreams } from '../../../../scene/integrations/config';
import { MEDIA_IMPORT_FIELDS, STREAM_NAMES } from '../../../../scene/integrations/types';
import { getStreamFormOrder } from '../../../../utils/formUtils';
import { ETL_INTEGRATION_TYPE } from '../../../configuration/types';

export const MediaImport: React.FC = () => {
  const { isInReadOnlyMode, integrationType } = useIntegrations();
  const { t } = useTranslation();

  const availableStreams = useMemo(() => getSupportedStreams(integrationType), [integrationType]);
  const mediaImportOrder = getStreamFormOrder(STREAM_NAMES.mediaImport, availableStreams);

  const { input: enabledInput } = useField(MEDIA_IMPORT_FIELDS(mediaImportOrder).enabled);
  const {
    mutators: { push },
  } = useForm();

  const canAddNewField = (fieldValues: string[]): boolean => {
    return !!fieldValues[fieldValues.length - 1];
  };

  const addNewField = (fieldValues: string[]) => {
    if (canAddNewField(fieldValues)) {
      push(MEDIA_IMPORT_FIELDS(mediaImportOrder).azureConnectionStrings, '');
    }
  };

  return (
    <Stack direction="vertical" className="p-20 w-full" spacing={20}>
      <Switch
        className="w-full"
        isSelected={enabledInput.value}
        isDisabled={isInReadOnlyMode}
        {...enabledInput}
        testId={TEST_IDS.checkboxMediaImportEnabled}
      >
        {t(`myConfigurations.tasks.${enabledInput.value ? 'on' : 'off'}`)}
      </Switch>

      {integrationType === ETL_INTEGRATION_TYPE.msteams && (
        <>
          <FieldArray name={MEDIA_IMPORT_FIELDS(mediaImportOrder).azureConnectionStrings}>
            {({ fields }) => {
              return (
                <>
                  {fields.map((name, index) => (
                    <Flex gap={15} key={`azureConnectionString-${name}`}>
                      <AzureConnectionString
                        field={name}
                        isRequired={enabledInput.value && index === 0}
                        testId={TEST_IDS.mediaImportAzureConnectionString(index)}
                        label={t('myConfigurations.tasks.mediaImport.azureConnectionString')}
                      />
                      <Button
                        aria-label={t('myConfigurations.tasks.mediaImport.azureConnectionStringDelete')}
                        testId={TEST_IDS.mediaImportAzureConnectionStringDeleteButton(index)}
                        variant="quiet"
                        className={index === 0 ? 'invisible' : ''}
                        onClick={() => {
                          fields.remove(index);
                        }}
                        color="error"
                      >
                        <BinIcon />
                      </Button>
                    </Flex>
                  ))}
                  <Button
                    aria-label={t('myConfigurations.tasks.mediaImport.azureConnectionStringAdd')}
                    testId={TEST_IDS.mediaImportAzureConnectionAddButton}
                    variant="outline"
                    onClick={() => addNewField(fields.value)}
                    className="mie-auto"
                    isDisabled={!canAddNewField(fields.value)}
                  >
                    <PlusIcon />
                  </Button>
                </>
              );
            }}
          </FieldArray>
        </>
      )}
    </Stack>
  );
};
